import EarlyAccessPilotBadge from '@/components/environment/EarlyAccessPilotBadge';
import type {
  AttemptPhase,
  TeleopAttemptWsMeta,
  TeleopSessionEventEntry,
} from '@/app/Teleop/teleopAttemptTypes';

type ConnectionStatus = 'connected' | 'connecting' | 'disconnected';

type TeleopHudProps = {
  queueStatus: ConnectionStatus;
  controlPlaneStatus: ConnectionStatus;
  livekitStatus: ConnectionStatus;
  shakeAnimation: string;
  shouldShowEarlyAccessPilot: boolean;
  earlyAccessPilotPositionClass: string;
  attemptPhase: AttemptPhase;
  attemptIndex: number;
  maxAttempts: number;
  secondsLeft: number;
  awaitingResult: boolean;
  readyMessage: string | null;
  showDebugPanel: boolean;
  lastWsMeta: TeleopAttemptWsMeta | null;
  sessionId: string;
  sessionEventLog: TeleopSessionEventEntry[];
  formatSeconds: (totalSeconds: number) => string;
};

type TeleopConnectionIndicator = {
  key: string;
  label: string;
  status: ConnectionStatus;
};

const getStatusColorClass = (status: ConnectionStatus) => {
  if (status === 'connected') {
    return 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.65)]';
  }

  if (status === 'connecting') {
    return 'bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.6)]';
  }

  return 'bg-rose-400 shadow-[0_0_10px_rgba(248,113,113,0.6)]';
};

const buildTimerLabel = ({
  attemptPhase,
  attemptIndex,
  awaitingResult,
  secondsLeft,
  formatSeconds,
}: {
  attemptPhase: AttemptPhase;
  attemptIndex: number;
  awaitingResult: boolean;
  secondsLeft: number;
  formatSeconds: (totalSeconds: number) => string;
}) => {
  if (attemptPhase === 'done') {
    return 'ENDED';
  }

  if (attemptPhase === 'waiting') {
    const waitingLabel = awaitingResult
      ? 'RESOLVING'
      : attemptIndex <= 1
        ? 'READY'
        : 'RESETTING';

    return waitingLabel;
  }

  if (attemptPhase === 'countdown') {
    return 'GET READY';
  }

  return formatSeconds(secondsLeft);
};

const buildTimerClass = (attemptPhase: AttemptPhase) => {
  if (attemptPhase === 'waiting') {
    return 'text-amber-200';
  }

  if (attemptPhase === 'countdown') {
    return 'text-amber-200';
  }

  if (attemptPhase === 'done') {
    return 'text-white/70';
  }

  return 'text-white';
};

const TeleopHud: React.FC<TeleopHudProps> = ({
  queueStatus,
  controlPlaneStatus,
  livekitStatus,
  shakeAnimation,
  shouldShowEarlyAccessPilot,
  earlyAccessPilotPositionClass,
  attemptPhase,
  attemptIndex,
  maxAttempts,
  secondsLeft,
  awaitingResult,
  readyMessage,
  showDebugPanel,
  lastWsMeta,
  sessionId,
  sessionEventLog,
  formatSeconds,
}) => {
  const connectionIndicators: TeleopConnectionIndicator[] = [
    {
      key: 'queue',
      label: 'Queue',
      status: queueStatus,
    },
    {
      key: 'control',
      label: 'Control',
      status: controlPlaneStatus,
    },
    {
      key: 'livekit',
      label: 'VIDEO',
      status: livekitStatus,
    },
  ];
  const attemptLabel = `Attempt ${attemptIndex}/${maxAttempts}`;
  const timerLabel = buildTimerLabel({
    attemptPhase,
    attemptIndex,
    awaitingResult,
    secondsLeft,
    formatSeconds,
  });
  const timerClass = buildTimerClass(attemptPhase);
  const readyDetail = attemptPhase === 'waiting' ? readyMessage : null;

  return (
    <>
      <div className="absolute left-4 top-[calc(env(safe-area-inset-top,0px)+16px)] z-10 max-[900px]:left-3 max-[600px]:left-2 max-[600px]:top-[calc(env(safe-area-inset-top,0px)+12px)]">
        <div
          className="flex items-center gap-3 rounded-full border border-white/10 bg-black/35 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-white/70 shadow-[0_12px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl"
          style={{ animation: shakeAnimation }}
        >
          {connectionIndicators.map((indicator) => (
            <div key={indicator.key} className="flex items-center gap-1.5">
              <span
                className={`h-2.5 w-2.5 rounded-full ${getStatusColorClass(indicator.status)}`}
              />
              <span>{indicator.label}</span>
            </div>
          ))}
        </div>
      </div>
      {shouldShowEarlyAccessPilot ? (
        <div
          className={`pointer-events-none absolute z-10 ${earlyAccessPilotPositionClass}`}
        >
          <EarlyAccessPilotBadge className="bg-black/35 text-amber-100" />
        </div>
      ) : null}
      <div className="absolute right-[88px] top-[calc(env(safe-area-inset-top,0px)+28px)] z-10 max-[900px]:right-[72px] max-[900px]:top-[calc(env(safe-area-inset-top,0px)+30px)] max-[600px]:right-3 max-[600px]:top-[calc(env(safe-area-inset-top,0px)+12px)]">
        <div className="rounded-full border border-white/10 bg-white/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80 shadow-[0_12px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl">
          <div className="text-white/60">{attemptLabel}</div>
          <div className={`text-xs font-semibold tracking-normal ${timerClass}`}>
            {timerLabel}
          </div>
          {readyDetail ? (
            <div className="text-[9px] font-semibold tracking-[0.16em] text-white/60">
              {readyDetail}
            </div>
          ) : null}
        </div>
      </div>
      {showDebugPanel ? (
        <div className="absolute left-4 top-[calc(env(safe-area-inset-top,0px)+48px)] z-10 max-[900px]:left-3 max-[900px]:top-[calc(env(safe-area-inset-top,0px)+52px)] max-[600px]:top-[calc(env(safe-area-inset-top,0px)+56px)]">
          <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70 shadow-[0_10px_24px_rgba(0,0,0,0.35)] backdrop-blur-xl">
            <div>WS {lastWsMeta?.type ?? '—'}</div>
            <div className="text-[9px] font-semibold tracking-[0.12em] text-white/60">
              sess {lastWsMeta?.sessionId?.slice(0, 8) ?? '—'} / cur{' '}
              {sessionId.slice(0, 8)}
            </div>
            <div className="text-[9px] font-semibold tracking-[0.12em] text-white/60">
              att {lastWsMeta?.attemptId?.slice(0, 8) ?? '—'} dur{' '}
              {lastWsMeta?.durationSeconds ?? '—'}
            </div>
            {sessionEventLog.length > 0 ? (
              <div className="mt-2 space-y-1 text-[9px] font-semibold tracking-[0.1em] text-white/60">
                {sessionEventLog.map((entry, index) => (
                  <div key={`${entry.ts}-${index}`}>
                    {entry.eventType}
                    {entry.attemptId ? ` ${entry.attemptId.slice(0, 6)}` : ''}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default TeleopHud;
